import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import AZIENDA from '../config/azienda';
import { useTraduzioniOspite } from '../utils/linguaOspite';
import { PRINCIPALE, voce } from '../i18n';

// Il testo della privacy e dei termini resta in italiano: è il testo che fa fede. Tradotti sono il titolo, la data
// e l'avviso che lo dice (legali.avviso), così chi legge in un'altra lingua sa subito com'è.

// Data dell'ultima modifica: la stessa nei Termini di Servizio (TerminiServizio.jsx la importa da qui)
export const ULTIMO_AGGIORNAMENTO = 'settembre 2026';

// La pagina da cui si è arrivati: state.da (i link della scena di accesso e del profilo) o ?da= (i link della casella
// della registrazione, che si aprono in un'altra scheda). Solo percorsi di questo sito.
export const paginaDiProvenienza = (state, search) => {
  let da = typeof state?.da === 'string' ? state.da : null;
  if (!da) {
    try { da = new URLSearchParams(search || '').get('da'); } catch { da = null; }
  }
  return da && da.startsWith('/') && !da.startsWith('//') ? da : null;
};

const NOMI_RITORNO = [[/^\/register/, 'legali.torna_registrazione'], [/^\/login/, 'legali.torna_accesso']];

// Pagine legali: si aprono in cima (prima restava lo scorrimento della pagina di prima e si aprivano a metà), con il
// titolo della scheda, e "Torna" riporta dove si era (prima sempre a "/", quindi al login, anche dalla registrazione):
// indietro nella storia se ci si è arrivati da una pagina del sito, altrimenti alla pagina di provenienza o al sito
export const useApertura = (titolo) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const precedente = document.title;
    document.title = titolo;
    return () => { document.title = precedente; };
  }, [titolo]);
};

export const BottoneRitorno = () => {
  const { state, search, key } = useLocation();
  const navigate = useNavigate();
  const { t } = useTraduzioniOspite();
  const da = paginaDiProvenienza(state, search);
  const conStoria = key !== 'default'; // aperta da un'altra pagina del sito (non un link in una scheda nuova)
  const chiave = (da && NOMI_RITORNO.find(([re]) => re.test(da))?.[1]) || (conStoria || da ? 'legali.torna_indietro' : 'legali.torna_sito');
  const torna = () => {
    if (conStoria) navigate(-1);
    else navigate(da || '/');
  };
  return (
    <Button variant="ghost" className="hover-gold mb-8" onClick={torna} data-testid="torna-legale">
      <ArrowLeft className="h-4 w-4 mr-2" /> {t(chiave)}
    </Button>
  );
};

// L'avviso sopra al testo: compare solo a chi legge in una lingua in cui l'avviso c'è davvero (in italiano sarebbe
// una riga di troppo, e in una lingua non ancora tradotta un avviso in italiano non direbbe niente a nessuno)
export const AvvisoLingua = () => {
  const { t, l } = useTraduzioniOspite();
  if (voce(l, 'legali.avviso').da === PRINCIPALE) return null;
  return <p className="text-xs text-gold/80 border border-gold/30 rounded-lg px-3 py-2 mb-6" data-testid="avviso-lingua">{t('legali.avviso')}</p>;
};

const PrivacyPolicy = () => {
  const { t } = useTraduzioniOspite();
  useApertura('MK Client — Privacy');
  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <BottoneRitorno />

        <h1 className="text-4xl font-display font-bold text-gold mb-2 break-words">{t('legali.privacy')}</h1>
        <p className="text-muted-foreground mb-8">{t('legali.aggiornato', { data: t('legali.data') })}</p>
        <AvvisoLingua />

        <div className="space-y-8 text-sm leading-relaxed text-white/80">

          <section>
            <h2 className="text-xl font-display text-gold mb-3">1. Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati personali è <strong className="text-white">{AZIENDA.nome}</strong>,
              con sede in {AZIENDA.via}, {AZIENDA.cap} {AZIENDA.citta} ({AZIENDA.provincia}), P.IVA {AZIENDA.partitaIva},
              raggiungibile all'indirizzo email{' '}
              <a href="mailto:maksym@mkconsulting.it" className="text-gold hover:underline">maksym@mkconsulting.it</a>{' '}
              o al telefono <a href={AZIENDA.telefonoHref} className="text-gold hover:underline">{AZIENDA.telefono}</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">2. Dati raccolti</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong className="text-white">Registrazione e accesso:</strong> nome, cognome, email, telefono, password (conservata solo in forma cifrata), dispositivi con Face ID o impronta (solo la chiave pubblica: l'impronta non esce mai dal telefono)</li>
              <li><strong className="text-white">Profilo:</strong> nazionalità, stato civile, familiari (nome, data di nascita, relazione), lavoro e reddito, preferenze di ricerca, disponibilità per le visite, lingua</li>
              <li><strong className="text-white">Pratica immobiliare:</strong> link agli annunci e dati degli immobili, visite ed esiti (anche con foto), proposte d'acquisto, dati del mutuo</li>
              <li><strong className="text-white">Documenti:</strong> quelli che caricate voi o che carichiamo noi per la pratica (identità, reddito, conti, immobile, mutuo, rogito)</li>
              <li><strong className="text-white">Firme:</strong> l'immagine della firma fatta sul telefono, nome scritto, data e ora, indirizzo IP e tipo di dispositivo</li>
              <li><strong className="text-white">Dati tecnici:</strong> indirizzo IP, data e ora delle aperture e dei download dei documenti (registro degli accessi), ultimo accesso dello staff</li>
              <li><strong className="text-white">Google Calendar:</strong> solo se scegliete di collegarlo</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">3. Finalità</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Seguire la vostra ricerca e la pratica di acquisto: immobili, visite, proposta, mutuo, rogito</li>
              <li>Preparare e consegnare i documenti richiesti da banca e notaio</li>
              <li>Mandarvi avvisi e promemoria (visite, documenti da caricare o in scadenza, novità della pratica) per email e notifica sul telefono</li>
              <li>Raccogliere la firma di incarichi, informative e contratti</li>
              <li>Tenere sicuri i dati e sapere chi li ha aperti</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">4. Lettura automatica dei documenti</h2>
            <p>
              Quando caricate un documento, MK Client ne legge il testo (riconoscimento del testo, OCR) per capire che documento è,
              a quale periodo si riferisce e quando scade, e per proporre allo staff i dati da confermare (per esempio la data di
              nascita dalla carta d'identità). La lettura avviene sui nostri server con un programma installato da noi: i documenti
              non vengono mandati a servizi esterni di intelligenza artificiale. Il risultato lo verifica sempre una persona dello staff.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">5. Invio a banca e notaio</h2>
            <p>
              Alla banca e al notaio non mandiamo allegati: mandiamo un link personale, che si apre una volta sola da un dispositivo
              e scade da solo (3, 7 o 30 giorni). Registriamo quando viene aperto e cosa viene scaricato, e lo vedete anche voi nella
              pagina Documenti ("consegnato a…"). Per le banche possiamo preparare un fascicolo con i nomi dei file che chiedono.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">6. Firma elettronica</h2>
            <p>
              Alcuni documenti (incarico, informative, contratti) si firmano dal telefono con una firma elettronica semplice: disegnate la
              firma, scrivete nome e cognome e confermate di aver letto. Al documento aggiungiamo una pagina con la firma, data e ora,
              indirizzo IP, dispositivo e l'impronta digitale (SHA-256) del file prima della firma; anche il file firmato ha la sua impronta.
              Il documento firmato resta nei vostri documenti.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">7. Avvisi e promemoria</h2>
            <p>
              Gli avvisi arrivano per email, come notifica sul telefono (se la attivate) e nella campanella dell'app; per alcuni
              promemoria è predisposto WhatsApp. Le email partono dal nostro fornitore di invio (Resend). Le notifiche sul telefono si
              disattivano dalle impostazioni del browser o del telefono.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">8. Lettura degli annunci</h2>
            <p>
              I dati degli immobili (prezzo, foto, metri quadri, agenzia) li legge dall'annuncio pubblico un'estensione del browser
              usata nel nostro ufficio: apre le pagine dei portali come farebbe una persona, a ritmo lento, e ogni tanto ricontrolla
              prezzo e disponibilità. Non legge dati personali vostri e non accede ai vostri account sui portali.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">9. Dove stanno i dati e copie di sicurezza</h2>
            <p>
              I dati stanno su server nell'Unione Europea (database a Francoforte, file su Cloudflare R2 con sede dei dati in UE,
              applicazione in Europa). Ogni notte facciamo una copia di sicurezza del database, conservata in uno spazio privato;
              le copie servono solo a ripristinare i dati in caso di guasto.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">10. Base giuridica e conservazione</h2>
            <p>
              Trattiamo i dati per eseguire l'incarico di consulenza immobiliare (art. 6.1.b GDPR), per obblighi di legge
              (art. 6.1.c) e, per ciò che è facoltativo come Google Calendar o le notifiche sul telefono, con il vostro consenso
              (art. 6.1.a). Li conserviamo per la durata del rapporto e poi per il tempo richiesto dalla legge.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">11. Chi vede i dati</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong className="text-white">Lo staff di MK Consulting</strong> e il collaboratore che vi segue (ognuno vede solo i propri clienti)</li>
              <li><strong className="text-white">Banca e notaio</strong>, solo i documenti che consegniamo per la pratica, con il link che scade</li>
              <li><strong className="text-white">Fornitori tecnici</strong> (hosting, archiviazione dei file, invio email), che trattano i dati solo per conto nostro</li>
              <li><strong className="text-white">Google</strong>, limitatamente al calendario, solo se lo collegate</li>
            </ul>
            <p className="mt-2">I dati non vengono venduti né usati per pubblicità.</p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">12. I vostri diritti</h2>
            <p className="mb-2">
              Potete accedere ai vostri dati, correggerli, chiederne la cancellazione o la limitazione, opporvi al trattamento,
              revocare il consenso e chiedere la portabilità. Dal vostro profilo, con <strong className="text-white">Scarica i miei dati</strong>,
              ricevete in pochi minuti uno zip con i vostri dati e i vostri documenti (il link vale 24 ore). Per tutto il resto scrivete a{' '}
              <a href="mailto:maksym@mkconsulting.it" className="text-gold hover:underline">maksym@mkconsulting.it</a>.
              Potete anche rivolgervi al Garante per la protezione dei dati personali.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">13. Sicurezza</h2>
            <p>
              Password cifrate, connessioni HTTPS, accesso ai dati limitato per ruolo, documenti scaricabili solo con l'accesso,
              registro di aperture e download, link a banca e notaio che scadono da soli.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-display text-gold mb-3">14. Lingue</h2>
            <p>
              Questa informativa può essere disponibile in altre lingue. In caso di dubbio prevale la versione italiana.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
